import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { sizedUrl, srcSetFor } from "../lib/api";
import { BlurImage } from "./BlurImage";
import { useLang } from "../context/LanguageContext";

export const ProjectCard = ({ project, aspect = "aspect-[4/5]", index = 0 }) => {
  const { L, tc, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <motion.div
      ref={ref}
      data-testid={`project-card-${project.slug}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <Link to={`/project/${project.slug}`} className="block">
        <div className={`relative overflow-hidden bg-[#141312] ${aspect}`}>
          <motion.div style={{ y }} className="absolute inset-[-8%]">
            {project.cover_type === "video" ? (
              <BlurImage
                lqip={project.cover_lqip}
                wrapperClassName="w-full h-full"
                src={sizedUrl(project.cover_poster || project.cover, 800)}
                srcSet={srcSetFor(project.cover_poster || project.cover, [480, 800])}
                sizes="(max-width: 767px) 50vw, 33vw"
                alt={project.name}
                loading={index < 4 ? "eager" : "lazy"}
                style={{ objectPosition: project.cover_position || "center" }}
                className="w-full h-full object-cover transition-[transform,filter] duration-[1200ms] ease-out grayscale-[0.15] group-hover:grayscale-0 group-hover:scale-[1.04]"
              />
            ) : (
              <BlurImage
                lqip={project.cover_lqip}
                wrapperClassName="w-full h-full"
                src={sizedUrl(project.cover, 800)}
                srcSet={srcSetFor(project.cover, [480, 800])}
                sizes="(max-width: 767px) 50vw, 33vw"
                alt={project.name}
                loading={index < 4 ? "eager" : "lazy"}
                fetchPriority={index < 4 ? "high" : "auto"}
                style={{ objectPosition: project.cover_position || "center" }}
                className="w-full h-full object-cover transition-[transform,filter] duration-[1200ms] ease-out grayscale-[0.15] group-hover:grayscale-0 group-hover:scale-[1.04]"
              />
            )}
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-700" />
          <div className="absolute top-4 right-4 hidden md:flex flex-wrap justify-end gap-1.5 max-w-[80%]">
            {(project.categories && project.categories.length ? project.categories : [project.category]).filter(Boolean).map((c) => (
              <span key={c} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-fg)]/70 border border-[var(--vsl-border)] px-2 py-1 rounded-full backdrop-blur-sm">
                {tc(c)}
              </span>
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex items-end justify-between">
            <div className="translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
              <h3 className="font-display text-2xl md:text-4xl leading-none text-[var(--vsl-on-media)]">{L(project, "name")}</h3>
            </div>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500 whitespace-nowrap">
              {t("card_view")} →
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
